/**
 * SVG (レイヤー素材) のアップロード規則 (docs/features/motion-graphics-mode-plan.md §3.2・§3.5・§5.2)。
 *
 * - `.svg` / `image/svg+xml` を受ける。backend は拡張子と MIME の両方を要求するので、OS が MIME を
 *   付けない場合 (空文字) は拡張子から `image/svg+xml` を補う
 * - 16 MB まで (Flask の MAX_CONTENT_LENGTH と同じ)。超過はアップロード前に止める (正本は backend の
 *   backend/services/motion_layered/svg_allowlist.py の LIMITS['maxSvgUploadBytes']。ここは事前検査用の写し)
 * - 受け付けるのは 3 タブ目「モーショングラフィックス」が見える利用者だけ (useSvgLayerUploadGate =
 *   useMotionGraphicsTabVisible と同じ条件 = ブランドのフラグ かつ 暫定ゲートを通る・SVG が来たときに確認)。
 *   見えない利用者の SVG は「対応していない形式」で弾く
 * - GCS 直送 (resumable) には乗せず、必ず multipart の `POST /api/materials/{temporary,cloud}/upload`
 *   (解析経路) に送る。本体は backend が作る合成 PNG になる
 */

export const SVG_MIME = 'image/svg+xml';
export const SVG_LAYER_MAX_BYTES = 16 * 1024 * 1024;
export const SVG_LAYER_TOO_LARGE_MESSAGE = 'SVG は 16 MB までです';
export const SVG_NOT_SUPPORTED_MESSAGE = '対応していない形式です (SVG)';
/**
 * アップロード中の表示 (解析に数秒〜最大 90 秒かかる・画像を含む SVG)。表示は multipart の応答まで出し続ける。
 * apiClient の timeout は 360 秒 (services/apiClient.ts) なので 90 秒の解析で切れない
 */
export const SVG_LAYER_LOADING_LABEL = 'レイヤーを読み込んでいます';
/** <input type="file" accept> に足す値 (image/* に SVG を含めない OS / ブラウザがあるため明示する) */
export const SVG_UPLOAD_ACCEPT = '.svg,image/svg+xml';

type FileLike = { name: string; type: string };

export function isSvgFile(file: FileLike): boolean {
  return file.type === SVG_MIME || file.name.toLowerCase().endsWith('.svg');
}

/** 素材としてアップロードできる種類か (画像 / 動画 / 音声 / SVG)。SVG の可否とサイズは svgUploadPrecheckError で見る */
export function isUploadableMaterialFile(file: FileLike): boolean {
  return file.type.startsWith('image/')
    || file.type.startsWith('video/')
    || file.type.startsWith('audio/')
    || isSvgFile(file);
}

/** <input accept> の値。SVG を受ける利用者にだけ `.svg,image/svg+xml` を足す */
export function withSvgAccept(base: string, allowSvg: boolean): string {
  return allowSvg ? `${base},${SVG_UPLOAD_ACCEPT}` : base;
}

/** GCS 直送 (resumable) の対象か。動画 / 音声のみ。SVG は解析経路なので常に false */
export function isDirectUploadTarget(file: FileLike): boolean {
  if (isSvgFile(file)) return false;
  return file.type.startsWith('video/') || file.type.startsWith('audio/');
}

/**
 * アップロード前の SVG の検査。問題が無ければ null。
 * - SVG を受けない利用者 (allowSvg=false) → 「対応していない形式です (SVG)」
 * - 16 MB 超 → 「SVG は 16 MB までです」
 */
export function svgUploadPrecheckError(
  file: FileLike & { size: number },
  { allowSvg }: { allowSvg: boolean },
): string | null {
  if (!isSvgFile(file)) return null;
  if (!allowSvg) return SVG_NOT_SUPPORTED_MESSAGE;
  if (file.size > SVG_LAYER_MAX_BYTES) return SVG_LAYER_TOO_LARGE_MESSAGE;
  return null;
}

export class SvgUploadPrecheckError extends Error {
  errorCode = 'SVG_TOO_LARGE';
  constructor(message: string) {
    super(message);
    this.name = 'SvgUploadPrecheckError';
  }
}

/**
 * multipart 送信の直前に通す。SVG なら 16 MB を超えていれば SvgUploadPrecheckError を投げ、
 * MIME が空 / 別の値なら `image/svg+xml` を付け直した File を返す。SVG 以外はそのまま返す。
 */
export function prepareMaterialUploadFile(file: File): File {
  if (!isSvgFile(file)) return file;
  // 公開範囲 (allowSvg) は UI 側で判定済み。ここではサイズだけ見る (公開範囲の最終判定は backend)
  const precheck = svgUploadPrecheckError(file, { allowSvg: true });
  if (precheck) throw new SvgUploadPrecheckError(precheck);
  if (file.type === SVG_MIME) return file;
  return new File([file], file.name, { type: SVG_MIME, lastModified: file.lastModified });
}
